import { type LiveChannelFormatted } from '$/channels'
import {
  CardsBreakpointsGrid,
  CardType,
  COLLECTION_GAPS,
  FIXED_INDENTS,
  IOverlayLayoutProps,
  LAYOUT,
  LiveChannelCard,
  UVOTestGridTemplate,
  type ITestGridProps,
} from '@setplex/arcane'
import { LiveChannel, MediaTypes } from '@setplex/tria-api'
import { useUnit } from 'effector-react'
import { JSX, type SyntheticEvent } from 'react'
import { epgCategoriesModel } from '~/entities/epg/categories'
import { model as session } from '~/entities/session'
import { model as favoritesModel } from '~/features/favorites'
import { addGoogleAnalyticsPrefixWithAuth } from '~/shared/helpers'

interface IRowProps {
  className?: string
  content?: LiveChannelFormatted[] | LiveChannel[] | null
  title?: ITestGridProps['title']
  overlayEnabled?: IOverlayLayoutProps['overlayEnabled']
  overlayWidth?: IOverlayLayoutProps['overlayWidth']
  overlayHeight?: IOverlayLayoutProps['overlayHeight']
  overlayHorizontalColor?: IOverlayLayoutProps['overlayHorizontalColor']
  overlayVerticalColor?: IOverlayLayoutProps['overlayVerticalColor']
  backgroundPosterEnabled?: ITestGridProps['backgroundPosterEnabled']
  backgroundPosterUrl?: ITestGridProps['backgroundPosterUrl']
  marginTitle?: ITestGridProps['marginTitle']
  updatedTitle?: boolean // New Text component will be rendered in Grid if true
  onLoadMoreItems?: () => void
  paddings?: (FIXED_INDENTS | LAYOUT)[]
  margins?: FIXED_INDENTS[]
  onClickChannelCard?: () => void
  resetEpgBoard?: () => void
  isLoading?: boolean
  skeletonCardsQuantity?: number
  columnGap?: FIXED_INDENTS | COLLECTION_GAPS
  rowGap?: FIXED_INDENTS | COLLECTION_GAPS
  setTimeshift?: (
    timeshift: { enabled: boolean; shift: number; id: number } | null
  ) => void
}

ChannelGrid.displayName = 'ChannelGrid'
export function ChannelGrid({
  content,
  className = '',
  title = '',
  marginTitle,
  onLoadMoreItems,
  onClickChannelCard,
  resetEpgBoard,
  isLoading,
  skeletonCardsQuantity,
  columnGap = FIXED_INDENTS.INDENT_40,
  rowGap = FIXED_INDENTS.INDENT_40,
  setTimeshift,
  ...rest
}: IRowProps): JSX.Element {
  const favorite = useUnit(favoritesModel.$favorites)
  const handleFavoriteBtnClick = useUnit(favoritesModel.handleFavoriteBtnClick)
  const isAuthenticated = useUnit(session.$isAuthenticated)

  // regionId of the most recently loaded categories and channels
  const regionId = useUnit(epgCategoriesModel.$regionId)

  return (
    <UVOTestGridTemplate
      title={title}
      updatedTitle
      onLoadMoreItems={onLoadMoreItems}
      className={className}
      breakpoints={CardsBreakpointsGrid.ChannelCard}
      marginTitle={marginTitle}
      isLoading={isLoading}
      skeletonCardsQuantity={skeletonCardsQuantity}
      cardType={CardType.LiveChannelCard}
      columnGap={columnGap}
      rowGap={rowGap}
      {...rest}
    >
      {content?.length
        ? content.map((card, index) => {
            const {
              id,
              logo,
              link,
              title: channelTitle,
              number: channelNumber,
              productionCountries,
              channelOwnerId,
              timeshift,
            } = card

            const firstProductionCountryId = productionCountries?.[0]?.id

            const isFavorite = Boolean(favorite.live_channel[id])
            const onClick = (e: SyntheticEvent) => {
              e.preventDefault()
              e.stopPropagation()

              handleFavoriteBtnClick?.({
                id,
                isFavorite: !isFavorite,
                contentType: MediaTypes.LIVE_CHANNEL,
                publisherId: channelOwnerId,
                contentTitle: channelTitle,
              })
            }
            const idForGA = addGoogleAnalyticsPrefixWithAuth(
              `add-fav-channel-${id}`,
              isAuthenticated
            )

            const handleClickChannelCard = () => {
              // reset EPG board if the channel is from a different region
              if (firstProductionCountryId !== regionId) {
                resetEpgBoard?.()
              }

              if (setTimeshift) {
                setTimeshift({ ...timeshift, id })
              }

              onClickChannelCard?.()
            }

            return (
              <LiveChannelCard
                key={`${index}${id}`}
                href={link}
                playLinkId={addGoogleAnalyticsPrefixWithAuth(
                  `channel-play-${id}`,
                  isAuthenticated
                )}
                idForGA={idForGA}
                posterUrl={logo}
                isFavorite={isFavorite}
                handleFavoriteClick={onClick}
                onClick={handleClickChannelCard}
                alt={`"${channelTitle}" channel logo`}
                channelNumber={String(channelNumber)}
                isTimeshiftEnabled={timeshift?.enabled}
              />
            )
          })
        : null}
    </UVOTestGridTemplate>
  )
}
